import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserWithMembership } from '@/hooks/useUsers';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

interface UserPasswordResetDialogProps {
  user: UserWithMembership | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (newPassword: string) => Promise<boolean>;
  isResetting?: boolean;
}

const UserPasswordResetDialog: React.FC<UserPasswordResetDialogProps> = ({
  user,
  open,
  onOpenChange,
  onConfirm,
  isResetting = false,
}) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isFullScreen = isMobile || isTablet;
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError(t('users.resetPasswordDialog.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('users.resetPasswordDialog.passwordMismatch'));
      return;
    }

    const success = await onConfirm(newPassword);
    if (success) {
      setNewPassword('');
      setConfirmPassword('');
      onOpenChange(false);
    }
  };

  const handleClose = () => {
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    onOpenChange(false);
  };

  if (!user) return null;

  const content = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('users.resetPasswordDialog.description')}
      </p>
      
      <div className="space-y-2">
        <Label htmlFor="new-password">{t('users.resetPasswordDialog.newPassword')}</Label>
        <Input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          className="text-base"
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirm-password">{t('users.resetPasswordDialog.confirmPassword')}</Label>
        <Input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          className="text-base"
        />
      </div>

      {error && (
        <div className="text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isResetting}
          className="flex-1"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isResetting}
          className="flex-1"
        >
          {isResetting ? t('users.resetPasswordDialog.resetting') : t('users.resetPasswordDialog.resetPassword')}
        </Button>
      </div>
    </form>
  );

  if (isFullScreen) {
    return (
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="bottom"
          className={`${isTablet ? 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix' : 'h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix'}`}
        >
          <SheetHeader className={`${isTablet ? 'p-6 border-b flex-shrink-0' : 'p-6 border-b flex-shrink-0'}`}>
            <SheetTitle>{t('users.resetPasswordDialog.title')}</SheetTitle>
          </SheetHeader>
          <div className={`${isTablet ? 'flex-1 p-6 pb-safe-bottom overflow-y-auto' : 'flex-1 p-6 overflow-y-auto'}`}>
            {content}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.resetPasswordDialog.title')}</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default UserPasswordResetDialog;